import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Loader2, RefreshCw, Trash2, X, Plus, Award } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { apiCall, formatCents, dollarsToCents, type Coach } from '@/lib/api';

export default function Coaches() {
  const { getToken } = useAuth();
  const [items, setItems] = useState<Coach[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const token = await getToken();
      const r = await apiCall<{ items: Coach[] }>('/v1/coaches', { bearerToken: token });
      setItems(r.items);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Failed'); }
    finally { setLoading(false); }
  }, [getToken]);

  useEffect(() => { void load(); }, [load]);

  async function doDelete(id: string) {
    const token = await getToken();
    await apiCall(`/v1/coaches/${id}`, { method: 'DELETE', bearerToken: token });
    setItems((prev) => prev.filter((x) => x.id !== id));
    setPendingDelete(null);
  }

  return (
    <div className="space-y-4 max-w-5xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Coaches</h1>
          <p className="text-sm text-muted-foreground">Instructors who teach classes.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Refresh
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)}>
            <Plus className="mr-1 h-4 w-4" /> Add coach
          </Button>
        </div>
      </div>

      {err && <Card><CardContent className="py-4 text-sm text-destructive">{err}</CardContent></Card>}

      {!err && !loading && items.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            <Award className="mx-auto mb-3 h-8 w-8 opacity-40" />
            No coaches yet. <button className="underline" onClick={() => setShowCreate(true)}>Add the first one.</button>
          </CardContent>
        </Card>
      )}

      {items.length > 0 && (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b">
                  <th className="px-3 py-2 font-medium">Name</th>
                  <th className="px-3 py-2 font-medium">Contact</th>
                  <th className="px-3 py-2 font-medium text-right">Hourly rate</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => {
                  const isDel = pendingDelete === c.id;
                  return (
                    <tr key={c.id} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="px-3 py-2 font-medium">{c.name}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {[c.email, c.phone].filter(Boolean).join(' · ') || '—'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{c.hourly_rate_cents ? formatCents(c.hourly_rate_cents) : '—'}</td>
                      <td className="px-3 py-2">
                        <Badge variant={c.status === 'active' ? 'default' : 'outline'}>{c.status}</Badge>
                      </td>
                      <td className="px-3 py-2 text-right">
                        {isDel ? (
                          <div className="inline-flex items-center gap-1">
                            <Button variant="destructive" size="sm" className="h-7 px-2" onClick={() => void doDelete(c.id)}>Yes</Button>
                            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setPendingDelete(null)}><X className="h-3.5 w-3.5" /></Button>
                          </div>
                        ) : (
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-muted-foreground hover:text-destructive" onClick={() => setPendingDelete(c.id)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {showCreate && (
        <AddCoachModal
          onClose={() => setShowCreate(false)}
          onCreated={(c) => { setItems((prev) => [...prev, c].sort((a, b) => a.name.localeCompare(b.name))); setShowCreate(false); }}
        />
      )}
    </div>
  );
}

function AddCoachModal({ onClose, onCreated }: { onClose: () => void; onCreated: (c: Coach) => void }) {
  const { getToken } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [bio, setBio] = useState('');
  const [rateStr, setRateStr] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true); setErr(null);
    try {
      const token = await getToken();
      const c = await apiCall<Coach>('/v1/coaches', {
        method: 'POST',
        bearerToken: token,
        body: {
          name: name.trim(),
          email: email.trim() || null,
          phone: phone.trim() || null,
          bio: bio.trim() || null,
          hourly_rate_cents: dollarsToCents(rateStr),
        },
      });
      onCreated(c);
    } catch (e2) { setErr(e2 instanceof Error ? e2.message : 'Failed'); }
    finally { setSubmitting(false); }
  }

  const inp = 'w-full rounded-md border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-card text-card-foreground rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h2 className="text-base font-semibold">Add coach</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <div><label className="block text-xs font-medium text-muted-foreground mb-1">Name *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required autoFocus className={inp} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-muted-foreground mb-1">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inp} /></div>
            <div><label className="block text-xs font-medium text-muted-foreground mb-1">Phone</label>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} className={inp} /></div>
          </div>
          <div><label className="block text-xs font-medium text-muted-foreground mb-1">Hourly rate (USD)</label>
            <input inputMode="decimal" placeholder="0.00" value={rateStr} onChange={(e) => setRateStr(e.target.value)} className={inp} /></div>
          <div><label className="block text-xs font-medium text-muted-foreground mb-1">Bio</label>
            <textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={3} className={inp + ' resize-none'} /></div>
          {err && <p className="text-sm text-destructive">{err}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={!name.trim() || submitting}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Add
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
